import { useTheme, Box, Typography, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIcon from "@mui/icons-material/Close";

import { hexToRgba } from "@aclymatepackages/converters";

const StatusIcon = ({ status, palette }) => {
  if (status === "captured") {
    return (
      <CheckCircleIcon
        fontSize="small"
        style={{ color: palette.secondary.main }}
      />
    );
  }
  return (
    <RadioButtonUncheckedIcon
      fontSize="small"
      style={{ color: palette.backgroundGray.dark }}
    />
  );
};

const SummaryRow = ({ entry, palette, onEdit }) => {
  const hasValue =
    entry.value !== undefined && entry.value !== null && entry.value !== "";
  const status = hasValue ? "captured" : "pending";
  return (
    <Box
      px={2}
      py={1.5}
      display="flex"
      alignItems="center"
      style={{
        gap: 12,
        borderRadius: 8,
        backgroundColor: entry.isCurrent
          ? hexToRgba(palette.primary.light, 0.15)
          : "transparent",
        border: entry.isCurrent
          ? `1px solid ${palette.primary.main}`
          : "1px solid transparent"
      }}
    >
      <StatusIcon status={status} palette={palette} />
      <Box flexGrow={1} minWidth={0} style={{ wordBreak: "break-word" }}>
        <Typography variant="caption" color="textSecondary">
          {entry.label}
          {entry.required && (
            <Typography component="span" variant="caption" color="secondary">
              {" "}
              · required
            </Typography>
          )}
        </Typography>
        <Typography
          variant="body2"
          color={hasValue ? "textPrimary" : "textSecondary"}
        >
          {hasValue ? entry.value : entry.placeholderText || "Not set yet"}
        </Typography>
      </Box>
      {hasValue && onEdit && (
        <Tooltip title="Edit this answer">
          <IconButton size="small" onClick={() => onEdit(entry.fieldId)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const entryHasValue = (entry) =>
  entry.value !== undefined && entry.value !== null && entry.value !== "";

const isVisibleEntry = (entry) =>
  entryHasValue(entry) || entry.alwaysShow || entry.required;

const SurveyCapturedSummaryShell = ({
  title,
  subtitle,
  entries = [],
  onEdit,
  isMobileDrawer = false,
  onCloseMobile
}) => {
  const { palette } = useTheme();
  const visibleEntries = entries.filter(isVisibleEntry);
  return (
    <Box
      sx={{
        display: isMobileDrawer ? "flex" : { xs: "none", md: "flex" },
        flex: "1 1 40%",
        maxWidth: isMobileDrawer ? "100%" : "40%",
        minWidth: isMobileDrawer ? "100%" : 280
      }}
      flexDirection="column"
      style={{
        background: palette.backgroundGray.light,
        height: "100%",
        overflowY: "auto",
        padding: 24
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={1}
      >
        <Box>
          <Typography variant="subtitle1" color="textPrimary">
            {title || "Your answers so far"}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {isMobileDrawer && onCloseMobile && (
          <IconButton size="small" onClick={onCloseMobile}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
        {visibleEntries.map((entry, idx) => (
          <SummaryRow
            key={`summary-row-${entry.fieldId || idx}`}
            entry={entry}
            palette={palette}
            onEdit={onEdit}
          />
        ))}
        {!visibleEntries.length && (
          <Typography variant="body2" color="textSecondary">
            Answer Scout&apos;s questions on the left — your responses will appear
            here.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SurveyCapturedSummaryShell;
